import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const GUEST_KEY = "md_guest_cart";
const loadGuest = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_KEY)) || []; } catch { return []; }
};
const saveGuest = (items) => localStorage.setItem(GUEST_KEY, JSON.stringify(items));

export const CartProvider = ({ children }) => {
  const { user, authReady } = useAuth();
  const [items, setItems] = useState([]);

  const refresh = useCallback(async () => {
    if (user) {
      const { data } = await api.get("/cart");
      setItems(data.items);
    } else {
      setItems(loadGuest());
    }
  }, [user]);

  useEffect(() => {
    const sync = async () => {
      if (!authReady) return;
      if (user) {
        const guest = loadGuest();
        if (guest.length) {
          try {
            await api.post("/cart/merge", { items: guest.map((i) => ({ product_id: i.id, qty: i.qty })) });
            saveGuest([]);
          } catch (e) { /* ignore */ }
        }
        const { data } = await api.get("/cart");
        setItems(data.items);
      } else {
        setItems(loadGuest());
      }
    };
    sync();
  }, [user, authReady]);

  const addItem = async (product, qty = 1) => {
    if (user) {
      const { data } = await api.post("/cart", { product_id: product.id, qty });
      setItems(data.items);
    } else {
      const guest = loadGuest();
      const found = guest.find((i) => i.id === product.id);
      const next = found
        ? guest.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i))
        : [...guest, { ...product, qty }];
      saveGuest(next);
      setItems(next);
    }
  };

  const removeItem = async (id) => {
    if (user) {
      const { data } = await api.delete(`/cart/${id}`);
      setItems(data.items);
    } else {
      const next = loadGuest().filter((i) => i.id !== id);
      saveGuest(next);
      setItems(next);
    }
  };

  const updateQty = async (id, qty) => {
    if (qty < 1) return removeItem(id);
    if (user) {
      const { data } = await api.put(`/cart/${id}`, { qty });
      setItems(data.items);
    } else {
      const next = loadGuest().map((i) => (i.id === id ? { ...i, qty } : i));
      saveGuest(next);
      setItems(next);
    }
  };

  const clearCart = async () => {
    if (user) { await api.delete("/cart"); setItems([]); }
    else { saveGuest([]); setItems([]); }
  };

  const createOrder = async (promoCode = null) => {
    const { data } = await api.post("/orders", { promo_code: promoCode });
    setItems([]);
    return data;
  };

  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, createOrder, count, total, refresh }}>
      {children}
    </CartContext.Provider>
  );
};
